import * as THREE from 'three'

import { Vec3 } from '../types'

/**
 * Roblox の Sparkles エフェクトを模したキラキラパーティクル。
 *
 * 加算合成のビルボード（Sprite）を Part の表面から湧かせ、回転しながら
 * 上昇・明滅させて「キラキラ」を近似する（実機 RJF08 のワープブロック等。
 * SparkleColor は白）。テクスチャは canvas に描いた十字スパーク 1 枚を
 * 全パーティクルで共有する。
 *
 * fire と違い、パーティクルは**ワールド studs 単位**で動く。親
 * （MeshEntry.container）は Part 寸法でスケールされる契約のため、呼び出し側
 * （threeRenderer.applyState）が group に 1/size の逆スケールを掛けて打ち消す。
 * これにより薄い板（例: ワープブロック 10×1×10）でも、きらめきが潰れずに
 * 立ち上る。発生範囲だけ Part 寸法（コンストラクタ引数）に比例させる。
 */
export interface SparkleEffectHandle {
  /** container へ追加するパーティクル群のルート。 */
  readonly group: THREE.Group
  /** 1 フレーム分パーティクルを進める。dt は秒。 */
  update(dt: number): void
  /** テクスチャ・マテリアルを解放する。group の除去は呼び出し側が行う。 */
  dispose(): void
}

/** パーティクル数。実機の Sparkles はかなり密にきらめく。 */
const PARTICLE_COUNT = 64

/** パーティクル寿命の下限・上限（秒）。短めでパッと弾ける。 */
const LIFE_MIN_SECONDS = 0.5
const LIFE_MAX_SECONDS = 1.3

/**
 * きらめきの色変化（誕生 → 消滅）。実機 RJF08 の Sparkles は
 * SparkleColor=(1,1,1) の白。誕生直後は白熱、減衰でほんのり青白く。
 */
const COLOR_BIRTH = new THREE.Color('#ffffff')
const COLOR_DEATH = new THREE.Color('#cfe2ff')

/** 上昇量（studs）。実機のきらめきは Part からしっかり立ち上る。 */
const RISE_DISTANCE = 4.5
/** スプライトの最大スケール（studs）。 */
const SPRITE_SCALE_MAX = 1.6
/** 点滅の速さ（rad/秒）。瞬きがキラキラ感を作る。 */
const TWINKLE_SPEED = 18
/** スプライトの回転速度の上限（rad/秒）。星がくるくる回りながら昇る。 */
const SPIN_SPEED_MAX = 4

/** 十字スパーク 1 枚ぶんのテクスチャを生成する（白核 + 4 方向の光条）。 */
const createSparkTexture = (): THREE.CanvasTexture => {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (context) {
    const center = size / 2
    // 中心のグロー。
    const glow = context.createRadialGradient(
      center,
      center,
      0,
      center,
      center,
      center,
    )
    glow.addColorStop(0, 'rgba(255, 255, 255, 1)')
    glow.addColorStop(0.25, 'rgba(255, 255, 255, 0.5)')
    glow.addColorStop(1, 'rgba(255, 255, 255, 0)')
    context.fillStyle = glow
    context.fillRect(0, 0, size, size)
    // 縦横の光条（中心から外へ減衰するライン）。
    const ray = context.createLinearGradient(0, center, size, center)
    ray.addColorStop(0, 'rgba(255, 255, 255, 0)')
    ray.addColorStop(0.5, 'rgba(255, 255, 255, 0.9)')
    ray.addColorStop(1, 'rgba(255, 255, 255, 0)')
    context.fillStyle = ray
    context.fillRect(0, center - 1.5, size, 3)
    const rayVertical = context.createLinearGradient(center, 0, center, size)
    rayVertical.addColorStop(0, 'rgba(255, 255, 255, 0)')
    rayVertical.addColorStop(0.5, 'rgba(255, 255, 255, 0.9)')
    rayVertical.addColorStop(1, 'rgba(255, 255, 255, 0)')
    context.fillStyle = rayVertical
    context.fillRect(center - 1.5, 0, 3, size)
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

/** 1 パーティクルの状態。respawn のたびに初期化される。 */
interface SparkParticle {
  readonly sprite: THREE.Sprite
  readonly material: THREE.SpriteMaterial
  /** 経過秒。life を超えたら respawn する。 */
  age: number
  /** このサイクルの寿命（秒）。 */
  life: number
  /** 発生位置（ワールド studs、Part 中心基準）。 */
  baseX: number
  baseY: number
  baseZ: number
  /** 点滅位相。パーティクルごとにずらして一斉明滅を避ける。 */
  twinklePhase: number
  /** このサイクルの最大スケール（個体差でランダム）。 */
  scaleMax: number
  /** スプライトの回転速度（rad/秒、向きもランダム）。 */
  spinSpeed: number
}

/** 発生パラメータを乱数で振り直す。 */
const respawn = (particle: SparkParticle, halfExtents: Vec3): void => {
  // Part の XZ 全面（やや内側）× 上面付近から湧く。
  particle.baseX = (Math.random() * 2 - 1) * halfExtents.x * 0.9
  particle.baseZ = (Math.random() * 2 - 1) * halfExtents.z * 0.9
  particle.baseY = halfExtents.y * (0.4 + Math.random() * 0.6)
  particle.life =
    LIFE_MIN_SECONDS + Math.random() * (LIFE_MAX_SECONDS - LIFE_MIN_SECONDS)
  particle.age = 0
  particle.twinklePhase = Math.random() * Math.PI * 2
  particle.scaleMax = SPRITE_SCALE_MAX * (0.4 + Math.random() * 0.6)
  particle.spinSpeed = (Math.random() * 2 - 1) * SPIN_SPEED_MAX
}

/** 寿命進行度 t（0..1）に応じてスプライトの位置・色・透明度・スケールを反映する。 */
const applyParticle = (particle: SparkParticle, t: number): void => {
  particle.sprite.position.set(
    particle.baseX,
    particle.baseY + RISE_DISTANCE * t,
    particle.baseZ,
  )
  // 出現・消滅をなだらかに、間は点滅（twinkle）させる。
  const envelope = Math.min(1, t * 4) * (1 - t)
  const twinkle =
    0.65 +
    0.35 * Math.sin(particle.twinklePhase + t * particle.life * TWINKLE_SPEED)
  const scale = particle.scaleMax * envelope * twinkle
  particle.sprite.scale.set(scale, scale, scale)
  particle.material.rotation =
    particle.twinklePhase + t * particle.life * particle.spinSpeed
  particle.material.color.lerpColors(COLOR_BIRTH, COLOR_DEATH, t)
  particle.material.opacity = Math.min(1, envelope * twinkle * 1.4)
}

/**
 * キラキラエフェクトを生成する。返した group を対象オブジェクトの container に
 * 追加し（逆スケールは呼び出し側が掛ける）、描画ループから update(dt) を呼ぶこと。
 *
 * @param partSize 発生範囲の基準にする Part 寸法（studs）。
 */
export const createSparkleEffect = (partSize: Vec3): SparkleEffectHandle => {
  const halfExtents: Vec3 = {
    x: partSize.x / 2,
    y: partSize.y / 2,
    z: partSize.z / 2,
  }
  const texture = createSparkTexture()
  const group = new THREE.Group()
  // 本体メッシュより手前に加算合成で重ねる（fireEffect と同じ前後関係対策）。
  group.renderOrder = 1

  const particles: SparkParticle[] = []
  for (let i = 0; i < PARTICLE_COUNT; i += 1) {
    const material = new THREE.SpriteMaterial({
      map: texture,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    })
    const sprite = new THREE.Sprite(material)
    // クリック判定・遮蔽フェードのレイキャスト対象にしない（fireEffect と同じ理由）。
    sprite.raycast = () => undefined
    const particle: SparkParticle = {
      sprite,
      material,
      age: 0,
      life: LIFE_MIN_SECONDS,
      baseX: 0,
      baseY: 0,
      baseZ: 0,
      twinklePhase: 0,
      scaleMax: SPRITE_SCALE_MAX,
      spinSpeed: 0,
    }
    respawn(particle, halfExtents)
    // 初期フレームから「きらめいている途中」に見えるよう寿命をばらして開始する。
    particle.age = Math.random() * particle.life
    applyParticle(particle, particle.age / particle.life)
    particles.push(particle)
    group.add(sprite)
  }

  const update = (dt: number): void => {
    for (const particle of particles) {
      particle.age += dt
      if (particle.age >= particle.life) {
        respawn(particle, halfExtents)
      }
      applyParticle(particle, particle.age / particle.life)
    }
  }

  const dispose = (): void => {
    for (const particle of particles) {
      particle.material.dispose()
    }
    texture.dispose()
  }

  return { group, update, dispose }
}
